import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService, LoginResult } from '../../services/auth.service';
import { PasskeyService } from '../../services/passkey.service';

/**
 * Schritt 2 des Identifier-First-Login-Flows.
 *
 * Zeigt die Email aus Schritt 1 + Passwort-Feld + Alternativen
 * (Passkey, Magic-Link, Passwort-vergessen). Email-ändern springt
 * zurück zu Schritt 1.
 */
@Component({
  selector: 'app-login-auth-step',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink],
  template: `
    <div class="t2d-login__form-header">
      <h2 class="t2d-login__form-title">Anmelden</h2>
    </div>

    <div class="t2d-login__email-display">
      <div>
        <div class="t2d-login__email-display__label">Angemeldet als</div>
        <div class="t2d-login__email-display__email">{{ email }}</div>
      </div>
      <button type="button" class="t2d-login__email-display__edit"
              (click)="editEmail.emit()">Email ändern</button>
    </div>

    @if (errorMessage()) {
      <div class="t2d-login__error-banner" role="alert" aria-live="polite">
        <svg class="t2d-login__error-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-9a1 1 0 10-2 0v4a1 1 0 102 0V9zm-1-4a1 1 0 100 2 1 1 0 000-2z" clip-rule="evenodd" />
        </svg>
        {{ errorMessage() }}
      </div>
    }

    <form [formGroup]="form" (ngSubmit)="submit()" class="t2d-login__form" novalidate autocomplete="on">
      <div class="t2d-login__field">
        <label class="t2d-login__label" for="login-password">Passwort</label>
        <div class="t2d-login__input-wrapper">
          <svg class="t2d-login__input-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
          </svg>
          <input
            id="login-password"
            [type]="showPassword() ? 'text' : 'password'"
            autocomplete="current-password"
            formControlName="password"
            class="t2d-login__input t2d-login__input--password"
            placeholder="Ihr Passwort"
          />
          <button
            type="button"
            class="t2d-login__password-toggle"
            [attr.aria-label]="showPassword() ? 'Passwort verbergen' : 'Passwort anzeigen'"
            (click)="showPassword.set(!showPassword())">
            @if (!showPassword()) {
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
              </svg>
            } @else {
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd" />
                <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.064 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
              </svg>
            }
          </button>
        </div>
      </div>

      <div class="t2d-login__forgot">
        <a [routerLink]="['/password-vergessen']" [queryParams]="{ email: email }"
           class="t2d-login__forgot-link">Passwort vergessen?</a>
      </div>

      <button
        type="submit"
        class="t2d-login__submit"
        [disabled]="form.invalid || isLoading()">
        @if (isLoading()) { Anmelden… } @else { Anmelden }
      </button>

      <div class="t2d-login__divider">oder</div>

      <button
        type="button"
        class="t2d-login__btn-outline"
        [disabled]="isPasskeyBusy()"
        (click)="onPasskey()">
        @if (isPasskeyBusy()) { Authentifiziere… } @else { 🔑 Mit Passkey anmelden }
      </button>

      <p class="t2d-login__form-subtitle" style="text-align:center; margin-top: 0.5rem;">
        Probleme beim Anmelden?<br>
        <a class="t2d-login__forgot-link magic-link-footer"
           [routerLink]="['/login/magic-link']"
           [queryParams]="{ email: email }">
          Login-Link per Email anfordern ›
        </a>
      </p>
    </form>
  `,
})
export class LoginAuthStepComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly passkeyService = inject(PasskeyService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) email!: string;
  @Output() editEmail = new EventEmitter<void>();
  @Output() passwordSubmitResult = new EventEmitter<LoginResult>();

  protected readonly form = this.fb.nonNullable.group({
    password: ['', [Validators.required]],
  });

  protected readonly isLoading = signal(false);
  protected readonly isPasskeyBusy = signal(false);
  protected readonly showPassword = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  submit(): void {
    if (this.form.invalid || this.isLoading()) return;
    const password = this.form.value.password ?? '';
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.authService
      .login(this.email, password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result === 'passkey-2fa-required') {
            this.handlePasskey2fa();
            return;
          }
          this.isLoading.set(false);
          this.passwordSubmitResult.emit(result);
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.errorMessage.set(this.decodeError(err));
        },
      });
  }

  private handlePasskey2fa(): void {
    const challengeToken = this.authService.passkeyTwoFactorChallengeToken();
    if (!challengeToken) {
      this.isLoading.set(false);
      this.errorMessage.set('Interner Fehler: Passkey-Challenge-Token fehlt.');
      return;
    }
    this.passkeyService
      .loginTwoFactor(challengeToken)
      .then(() => {
        this.authService
          .loadCurrentUser()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.isLoading.set(false);
              this.authService.passkeyTwoFactorChallengeToken.set(null);
              this.router.navigate(['/dashboard']);
            },
            error: () => {
              this.isLoading.set(false);
              this.errorMessage.set('Passkey-Login erfolgreich, aber Profil konnte nicht geladen werden.');
            },
          });
      })
      .catch((err: unknown) => {
        this.isLoading.set(false);
        const message = err instanceof Error ? err.message : 'Vorgang abgebrochen';
        this.errorMessage.set('Passkey-Verifizierung fehlgeschlagen: ' + message);
      });
  }

  async onPasskey(): Promise<void> {
    this.isPasskeyBusy.set(true);
    this.errorMessage.set(null);
    try {
      await this.passkeyService.login(this.email);
      this.authService
        .loadCurrentUser()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.isPasskeyBusy.set(false);
            this.router.navigate(['/dashboard']);
          },
          error: () => {
            this.isPasskeyBusy.set(false);
            this.errorMessage.set('Passkey-Login geklappt, aber Profil konnte nicht geladen werden.');
          },
        });
    } catch (err) {
      this.isPasskeyBusy.set(false);
      const message = err instanceof Error ? err.message : 'Vorgang abgebrochen';
      this.errorMessage.set('Passkey-Login fehlgeschlagen: ' + message);
    }
  }

  private decodeError(err: HttpErrorResponse): string {
    if (err.status === 429) {
      const retryAfter = Number(err.headers?.get('Retry-After'));
      if (Number.isFinite(retryAfter) && retryAfter > 0) {
        const minuten = Math.ceil(retryAfter / 60);
        return `Zu viele Login-Versuche. Bitte ${minuten} Minute${minuten === 1 ? '' : 'n'} warten.`;
      }
      return 'Zu viele Login-Versuche. Bitte später erneut versuchen.';
    }
    if (err.status === 0) return 'Server nicht erreichbar. Bitte Verbindung prüfen.';
    if (err.status >= 500) return 'Server-Fehler. Bitte später erneut versuchen.';
    return 'Email-Adresse oder Passwort ist falsch.';
  }
}
